
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, User, Video, Command, Bell, Check, X, Building2, Users, Layout, FolderKanban, Sparkles, CheckCircle2, Zap, Sun, Moon, TrendingUp, Palette, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { characterStore } from '../utils/characterStore';
import { characterOnlineStatus } from '../utils/characterOnlineStatus';
import EcomGeneratorModal from './EcomGeneratorModal';

const normalizeCharacters = (list: any[]) => (list || []).filter(Boolean).map((c) => ({
  ...c,
  avatarUrl: c?.avatarUrl || c?.imageUrl || c?.coverUrl || c?.avatar_url || '',
  imageUrl: c?.imageUrl || c?.avatarUrl || c?.coverUrl || c?.avatar_url || '',
  coverUrl: c?.coverUrl || c?.avatarUrl || c?.imageUrl || c?.avatar_url || '',
}));

export const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [isIslandExpanded, setIsIslandExpanded] = useState(false);
  const [onlineCharacters, setOnlineCharacters] = useState<any[]>([]);
  const notifRef = useRef<HTMLDivElement>(null);
  const [showTools, setShowTools] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout, notifications, fetchNotifications, respondNotification } = useAuth();
  const [ecomModalOpen, setEcomModalOpen] = useState(false);
  const [pricing, setPricing] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/ecom/pricing');
        if (!r.ok) return;
        const j = await r.json();
        if (j && j.data) setPricing(j.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // 🔥 角色在线状态展示（基于缓存，不再轮询）
  useEffect(() => {
    if (user) {
      const localChars = normalizeCharacters(characterStore.load());
      const onlineChars = localChars.filter(c => {
        const isOnline = characterOnlineStatus.getStatus(c.id);
        return isOnline === true; // 只显示明确在线的角色
      });

      if (onlineChars.length > 0) {
        // 模拟逐个上线的排队动画效果
        let index = 0;
        const timer = setInterval(() => {
          if (index < onlineChars.length) {
            setOnlineCharacters(prev => [...prev, onlineChars[index]]);
            index++;
          } else {
            clearInterval(timer);
          }
        }, 800);
        return () => clearInterval(timer);
      }
    } else {
      setOnlineCharacters([]);
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const refreshNotifications = async () => {
    setNotifLoading(true);
    try {
      await fetchNotifications();
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    if (user) refreshNotifications();
  }, [user]);

  // 点击外部关闭通知/工具下拉
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setShowNotifs(false);
      }
      if (toolsRef.current && !toolsRef.current.contains(event.target as Node)) {
        setShowTools(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (location.pathname.startsWith('/admin') || location.pathname === '/login' || location.pathname === '/register') return null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const unreadCount = notifications.filter(n => n.status === 'UNREAD').length;

  const navLinks = [
    { to: '/', label: '首页', icon: <Sparkles size={14} /> },
    { to: '/generate', label: '工作台', icon: <Layout size={14} /> },
    { to: '/drawing', label: 'AI 绘画', icon: <Palette size={14} /> },
    { to: '/viral-clone', label: '爆款拆解', icon: <Zap size={14} /> },
    { to: '/tasks', label: '项目', icon: <FolderKanban size={14} /> },
    { to: '/characters', label: '角色库', icon: <Users size={14} /> },
  ];

  const toolLinks = [
    { to: '/ecommerce-pro', label: '电商数据平台', desc: '选品/拼图/卖点' },
    { to: '/ecommerce-workflows', label: '电商工作流', desc: '素材与流程' },
    { to: '/crossborder-dashboard', label: '跨境分析', desc: '电商数据总览' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-[9999]`} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center relative h-[80px]">
        
        {/* Left: Logo (Static) */}
        <div className="pointer-events-auto">
          <Link to="/" className="flex items-center gap-2 group relative">
            <div className="bg-[linear-gradient(135deg,#c99c2e,#6b8a9e,#f2e7d5,#6b8a9e,#c99c2e)] bg-[length:200%_auto] animate-text-shine text-slate-950 p-1.5 rounded-xl transition-transform group-hover:scale-110 shadow-lg">
               <Video size={18} className="text-slate-950" />
            </div>
            <div className="flex flex-col justify-center">
              <span className="text-lg font-bold tracking-tight text-transparent bg-clip-text bg-[linear-gradient(135deg,#f2e7d5,#c99c2e,#6b8a9e,#c99c2e,#f2e7d5)] bg-[length:220%_auto] animate-text-shine heading-display">CorinVision</span>
              <span className="text-[9px] font-mono text-slate-400 dark:text-slate-500 leading-none tracking-wider scale-90 origin-left">SORA 2 INSIDE</span>
            </div>
          </Link>
        </div>

        {/* Center: Dynamic Island (Hidden on mobile) */}
        <div 
          className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-0 pointer-events-auto"
          onMouseEnter={() => setIsIslandExpanded(true)}
          onMouseLeave={() => setIsIslandExpanded(false)}
        >
          <motion.div
            initial={false}
            animate={{
              width: isIslandExpanded ? 620 : (onlineCharacters.length > 0 ? 220 + (onlineCharacters.length * 30) : 180),
              height: isIslandExpanded ? 62 : 50,
              borderRadius: isIslandExpanded ? 24 : 32,
              backgroundColor: isIslandExpanded ? "rgba(255, 255, 255, 0.95)" : "rgba(15, 23, 42, 0.85)",
              borderColor: isIslandExpanded ? "rgba(201, 156, 46, 0.2)" : "rgba(255, 255, 255, 0.1)",
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="flex items-center justify-center shadow-2xl backdrop-blur-xl border relative overflow-hidden group"
          >
            {/* Ambient Pulse (Idle State) */}
            <AnimatePresence>
              {!isIslandExpanded && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 flex items-center px-4 gap-3"
                >
                  <div className="flex items-center">
                    <motion.div 
                      animate={{
                        scale: [1, 1.4, 1],
                        opacity: [0.4, 0.8, 0.4],
                      }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      className="w-1.5 h-1.5 rounded-full bg-emerald-500 blur-[1px]"
                    />
                    <span className="ml-2 text-[9px] font-black text-white/60 tracking-[0.2em] uppercase whitespace-nowrap">System Online</span>
                  </div>

                  {/* Character Online Avatars */}
                  <div className="flex -space-x-2 ml-auto overflow-hidden">
                    <AnimatePresence>
                      {onlineCharacters.map((char, i) => (
                        <motion.div
                          key={char.id}
                          initial={{ x: 20, opacity: 0, scale: 0 }}
                          animate={{ x: 0, opacity: 1, scale: 1 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                          className="relative"
                        >
                          <div className="w-7 h-7 rounded-full border-2 border-slate-900 overflow-hidden bg-slate-800">
                            <img src={char.avatarUrl || char.imageUrl} className="w-full h-full object-cover" alt="" />
                          </div>
                          <motion.div 
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-slate-900 shadow-sm"
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Expanded Content */}
            <motion.div 
              className="flex items-center gap-1 px-2"
              animate={{ opacity: isIslandExpanded ? 1 : 0, scale: isIslandExpanded ? 1 : 0.8 }}
            >
              {navLinks.map((link) => {
                const isActive = link.to === '/' 
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.to);
                
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`
                      flex items-center gap-2 px-3.5 py-2 rounded-xl text-[13px] font-bold transition-all
                      ${isActive
                        ? 'bg-slate-900 text-white shadow-lg' 
                        : 'text-slate-500 hover:text-[#c99c2e] hover:bg-slate-50/50'}
                    `}
                  >
                    {link.icon}
                    {isIslandExpanded && <span>{link.label}</span>}
                  </Link>
                );
              })}
            </motion.div>
          </motion.div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3 pointer-events-auto">
          <button
            onClick={toggleTheme}
            className="p-2.5 rounded-2xl bg-[var(--cv-surface-2)] border border-[var(--cv-stroke)] text-[var(--cv-text-2)] hover:shadow-[var(--cv-glow-gold)] transition-all"
            aria-label={theme === 'dark' ? '切换到浅色主题' : '切换到暗色主题'}
            title={theme === 'dark' ? '切换到浅色主题' : '切换到暗色主题'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <div className="hidden md:block relative" ref={toolsRef}>
            <button
              onClick={() => setShowTools((prev) => !prev)}
              className="group flex items-center gap-2 px-3.5 py-2 rounded-2xl bg-[linear-gradient(135deg,#fff7e0,#fff2cc,#f8f4ea)] border border-[#c99c2e]/30 text-slate-700 hover:border-[#c99c2e]/60 hover:shadow-[0_8px_20px_rgba(201,156,46,0.25)] transition-all"
              title="电商入口"
              aria-label="电商入口"
            >
              <span className="p-1.5 rounded-xl bg-white/90 border border-[#c99c2e]/30 shadow-sm">
                <Command size={16} className="text-[#c99c2e]" />
              </span>
              <span className="text-xs font-black tracking-wide">电商入口</span>
              <span className="ml-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-900 text-white/90 group-hover:bg-slate-800">PRO</span>
            </button>
            <AnimatePresence>
              {showTools && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-3 w-72 bg-white/95 backdrop-blur-xl border border-[#c99c2e]/20 rounded-3xl shadow-[0_20px_50px_rgba(15,23,42,0.18)] overflow-hidden"
                >
                  <div className="px-4 py-4 border-b border-[#c99c2e]/15 bg-[linear-gradient(135deg,rgba(201,156,46,0.12),rgba(242,231,213,0.6))]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-black">Commerce Suite</p>
                    <p className="text-sm font-black text-slate-800">电商数据 · 工作流 · 分析</p>
                  </div>
                  <div className="py-2">
                    {toolLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setShowTools(false)}
                        className="group block px-4 py-2.5 hover:bg-[#f8f4ea] transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-semibold text-slate-700 group-hover:text-slate-900">{link.label}</div>
                          <span className="h-2 w-2 rounded-full bg-[#c99c2e]/60 group-hover:bg-[#c99c2e]" />
                        </div>
                        <div className="text-xs text-slate-400">{link.desc}</div>
                      </Link>
                    ))}
                    <div className="px-4 py-3 border-t">
                      <button onClick={() => setEcomModalOpen(true)} className="w-full px-3 py-2 bg-[#c99c2e] text-white rounded-lg font-bold">一键电商图</button>
                      <div className="text-xs text-slate-400 mt-2">套餐：{pricing ? `${pricing.price_cny} CNY` : '未配置'}</div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <EcomGeneratorModal open={ecomModalOpen} onClose={() => setEcomModalOpen(false)} />
          {user && (
            <div className="relative" ref={notifRef}>
              <button 
                onClick={() => { setShowNotifs(!showNotifs); if(!showNotifs) refreshNotifications(); }}
                className="p-2.5 rounded-2xl bg-white/80 border border-slate-200 text-slate-500 hover:bg-white transition-all shadow-sm"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-amber-500 rounded-full border-2 border-white animate-pulse" />
                )}
              </button>
              {/* Notification Dropdown (Keep existing logic but upgrade UI) */}
              <AnimatePresence>
                {showNotifs && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 top-full mt-3 w-80 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-100 overflow-hidden z-[100]"
                  >
                    <div className="p-4 border-b border-slate-50 flex justify-between items-center">
                      <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Notifications</span>
                      <button onClick={refreshNotifications} className="p-1.5 hover:bg-slate-50 rounded-lg text-slate-400 transition-colors">
                        <Check size={14} />
                      </button>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                      {/* ... existing notification list rendering ... */}
                      {notifications.map(n => (
                        <div key={n.id} className="p-4 hover:bg-slate-50/50 transition-colors border-b border-slate-50 last:border-0">
                          <p className="text-sm text-slate-700 leading-relaxed">{n.content}</p>
                          <span className="text-[10px] text-slate-400 mt-2 block font-medium">{new Date(n.created_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* 🔥 Mobile Menu Button (Hidden on md+) */}
          <motion.button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2.5 rounded-2xl bg-white/80 border border-slate-200 text-slate-900 hover:bg-white transition-all shadow-sm flex items-center justify-center relative"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Menu size={20} />
          </motion.button>

          {user ? (
            <div className="relative group ml-2">
               <button className="w-10 h-10 rounded-2xl bg-slate-900 flex items-center justify-center overflow-hidden shadow-lg transition-all hover:scale-110 active:scale-95 border border-white/20">
                  {user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : <User size={20} className="text-white" />}
               </button>
               
               {/* Dropdown */}
               <div className="absolute right-0 top-full mt-4 w-64 bg-white/95 backdrop-blur-xl rounded-[28px] shadow-[0_25px_50px_rgba(0,0,0,0.15)] border border-slate-100 p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all transform origin-top-right scale-95 group-hover:scale-100 z-[100]">
                  <div className="px-5 py-4 mb-2 bg-slate-50 rounded-[22px] border border-slate-100">
                    <p className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-black mb-1.5">
                        {user.tier === 'Team' ? '企业账户余额' : '当前积分点数'}
                    </p>
                    <div className="flex items-baseline gap-1">
                      <p className="text-2xl font-black text-slate-900 font-mono tracking-tighter">
                          {user.tier === 'Team' ? `¥${user.wallet_balance}` : user.points}
                      </p>
                      <span className="text-xs font-bold text-slate-400">{user.tier === 'Team' ? 'CNY' : 'POINTS'}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Link to="/profile" className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-xl text-sm font-bold text-slate-700 transition-colors">
                      <div className="p-1.5 bg-blue-50 text-blue-500 rounded-lg"><User size={16} /></div>
                      账号中心
                    </Link>
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 rounded-xl text-sm font-bold text-red-500 transition-colors">
                      <div className="p-1.5 bg-red-50 text-red-500 rounded-lg"><LogOut size={16} /></div>
                      退出登录
                    </button>
                  </div>
               </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login"><Button variant="ghost" size="sm">登录</Button></Link>
              <Link to="/register"><Button variant="black" size="sm">注册</Button></Link>
            </div>
          )}
        </div>
      </div>

      {/* 🔥 Mobile Menu Drawer */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            {/* Background Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileMenu(false)}
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden pointer-events-auto"
            />

            {/* Slide-in Menu */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-64 bg-white shadow-2xl md:hidden pointer-events-auto"
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <span className="font-bold text-slate-900">导航菜单</span>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-slate-500" />
                </button>
              </div>

              {/* Navigation Links */}
              <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
                <div className="py-2 space-y-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                    >
                      <span className="text-slate-500">{link.icon}</span>
                      <span className="font-semibold">{link.label}</span>
                    </Link>
                  ))}
                </div>

                {/* Divider */}
                <div className="h-px bg-slate-100 my-3" />

                {/* Tools Links */}
                <div className="py-2 space-y-1">
                  <div className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    电商工具
                  </div>
                  {toolLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setShowMobileMenu(false)}
                      className="block px-4 py-3 text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                    >
                      <div className="font-semibold text-sm">{link.label}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{link.desc}</div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Footer - Auth Links or User Info */}
              <div className="absolute bottom-0 left-0 right-0 border-t border-slate-100 bg-slate-50 p-4 space-y-2">
                {user ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <User size={16} />
                      账号中心
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-white rounded-lg text-sm font-bold text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <LogOut size={16} />
                      退出登录
                    </button>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Link to="/login" className="flex-1" onClick={() => setShowMobileMenu(false)}>
                      <Button variant="ghost" size="sm" fullWidth>
                        登录
                      </Button>
                    </Link>
                    <Link to="/register" className="flex-1" onClick={() => setShowMobileMenu(false)}>
                      <Button variant="black" size="sm" fullWidth>
                        注册
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};
